import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const CART_STORAGE_KEY = "ierryhouse:cart";
const WISHLIST_STORAGE_KEY = "ierryhouse:wishlist";

const readStorage = (keys, fallback) => {
  if (typeof window === "undefined") return fallback;

  for (const key of keys) {
    try {
      const storedValue = window.localStorage.getItem(key);
      if (storedValue) return JSON.parse(storedValue);
    } catch {
      // Storage can be unavailable in privacy mode. The in-memory state still works.
    }
  }

  return fallback;
};

const writeStorage = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A full or blocked localStorage should never prevent shopping in this session.
  }
};

const normalizeCart = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((items, item) => {
    if (!item || item.id === undefined || item.id === null) return items;

    const numericQuantity = Number(item.quantity);
    items.push({
      id: item.id,
      size: item.size || "",
      color: item.color || "",
      quantity: Number.isFinite(numericQuantity)
        ? Math.max(1, Math.floor(numericQuantity))
        : 1,
    });
    return items;
  }, []);
};

const normalizeWishlist = (value) => {
  if (!Array.isArray(value)) return [];

  return value.reduce((ids, item) => {
    const id = item && typeof item === "object" ? item.id : item;
    if (id === undefined || id === null) return ids;
    if (!ids.some((savedId) => String(savedId) === String(id))) ids.push(id);
    return ids;
  }, []);
};

const sameCartLine = (item, productId, size = "", color = "") =>
  String(item.id) === String(productId) &&
  item.size === (size || "") &&
  item.color === (color || "");

const getProductId = (productOrId) =>
  productOrId && typeof productOrId === "object"
    ? productOrId.id
    : productOrId;

export const CartContext = createContext({
  cartItems: [],
  wishlistItems: [],
  wishlist: [],
  cartCount: 0,
  wishlistCount: 0,
});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    normalizeCart(readStorage([CART_STORAGE_KEY], []))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    normalizeWishlist(readStorage([WISHLIST_STORAGE_KEY], []))
  );

  useEffect(() => {
    writeStorage(CART_STORAGE_KEY, cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStorage(WISHLIST_STORAGE_KEY, wishlistItems);
  }, [wishlistItems]);

  const addToCart = useCallback(
    (productId, quantity = 1, size = "", color = "") => {
      if (productId === undefined || productId === null) return;
      const parsedQuantity = Number(quantity);
      const amount = Number.isFinite(parsedQuantity)
        ? Math.max(1, Math.floor(parsedQuantity))
        : 1;

      setCartItems((previousItems) => {
        const existingItem = previousItems.find((item) =>
          sameCartLine(item, productId, size, color)
        );

        if (!existingItem) {
          return [
            ...previousItems,
            {
              id: productId,
              size: size || "",
              color: color || "",
              quantity: amount,
            },
          ];
        }

        return previousItems.map((item) =>
          sameCartLine(item, productId, size, color)
            ? { ...item, quantity: item.quantity + amount }
            : item
        );
      });
    },
    []
  );

  const removeFromCart = useCallback((productId, size = "", color = "") => {
    setCartItems((previousItems) =>
      previousItems.filter(
        (item) => !sameCartLine(item, productId, size, color)
      )
    );
  }, []);

  const updateQuantity = useCallback(
    (productId, size = "", color = "", quantity = 1) => {
      const parsedQuantity = Number(quantity);
      if (!Number.isFinite(parsedQuantity)) return;

      setCartItems((previousItems) =>
        previousItems.map((item) =>
          sameCartLine(item, productId, size, color)
            ? { ...item, quantity: Math.max(1, Math.floor(parsedQuantity)) }
            : item
        )
      );
    },
    []
  );

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((previousItems) =>
      previousItems.some((id) => String(id) === String(productId))
        ? previousItems
        : [...previousItems, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    setWishlistItems((previousItems) =>
      previousItems.filter((id) => String(id) !== String(productId))
    );
  }, []);

  const toggleWishlist = useCallback((productOrId) => {
    const productId = getProductId(productOrId);
    if (productId === undefined || productId === null) return;

    setWishlistItems((previousItems) =>
      previousItems.some((id) => String(id) === String(productId))
        ? previousItems.filter((id) => String(id) !== String(productId))
        : [...previousItems, productId]
    );
  }, []);

  const isWishlisted = useCallback(
    (productOrId) => {
      const productId = getProductId(productOrId);
      return wishlistItems.some((id) => String(id) === String(productId));
    },
    [wishlistItems]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const contextValue = useMemo(
    () => ({
      cartItems,
      wishlistItems,
      wishlist: wishlistItems,
      cartCount,
      wishlistCount: wishlistItems.length,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      addWishlist: addToWishlist,
      removeFromWishlist,
      removeWishlist: removeFromWishlist,
      toggleWishlist,
      isWishlisted,
    }),
    [
      addToCart,
      addToWishlist,
      cartCount,
      cartItems,
      clearCart,
      isWishlisted,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlistItems,
    ]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
